<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue';

// State for the install prompt event and visibility
const deferredPrompt = ref<any>(null);
const showInstallPrompt = ref(false);
const isIOS = ref(false);
const isAndroid = ref(false);
const installState = ref<'idle' | 'installing' | 'installed'>('idle');

// Function to install the PWA
const installApp = () => {
  if (!deferredPrompt.value) return;

  installState.value = 'installing';

  // Show the installation prompt
  deferredPrompt.value.prompt();

  // Wait for the user to respond to the prompt
  deferredPrompt.value.userChoice.then((choiceResult: { outcome: string }) => {
    if (choiceResult.outcome === 'accepted') {
    } else {
    }
    // Clear the saved prompt since it can't be used again
    deferredPrompt.value = null;
    showInstallPrompt.value = false;
  });
};

// Function to dismiss the install prompt
const dismissPrompt = () => {
  showInstallPrompt.value = false;
  // Store in localStorage that the user dismissed the prompt
  localStorage.setItem('pwaPromptDismissed', Date.now().toString());
};

// Function to detect device type
const detectDevice = () => {
  const userAgent = window.navigator.userAgent.toLowerCase();
  const isIOS = /iphone|ipad|ipod/.test(userAgent) && !window.MSStream;
  const isAndroid = /android/.test(userAgent);
  return { isIOS, isAndroid };
};

// Check if the prompt was recently dismissed (within 7 days)
const wasRecentlyDismissed = () => {
  const dismissedTime = localStorage.getItem('pwaPromptDismissed');
  if (!dismissedTime) return false;

  const dismissedDate = new Date(parseInt(dismissedTime));
  const now = new Date();
  const daysSinceDismissed = (now.getTime() - dismissedDate.getTime()) / (1000 * 3600 * 24);

  return daysSinceDismissed < 7; // Show again after 7 days
};

// Function to check if the app is already installed
const isAppInstalled = () => {
  return window.matchMedia('(display-mode: standalone)').matches ||
         window.matchMedia('(display-mode: fullscreen)').matches ||
         window.navigator.standalone === true;
};

onMounted(() => {
  // Don't show the prompt if the app is already installed
  if (isAppInstalled()) return;

  // Don't show the prompt if it was recently dismissed
  if (wasRecentlyDismissed()) return;

  // Detect device type
  const { isIOS: isiOS, isAndroid: isAndroidDevice } = detectDevice();
  isIOS.value = isiOS;
  isAndroid.value = isAndroidDevice;

  // For iOS devices, show the iOS-specific install instructions
  if (isIOS.value) {
    // Wait a few seconds before showing the prompt to avoid interrupting initial user experience
    setTimeout(() => {
      showInstallPrompt.value = true;
    }, 5000);
    return;
  }

  // For Android and other devices, use the beforeinstallprompt event
  window.addEventListener('beforeinstallprompt', (e) => {
    // Prevent the default browser install prompt
    e.preventDefault();

    // Save the event so it can be triggered later
    deferredPrompt.value = e;

    // For Android devices, show a more tailored prompt
    if (isAndroid.value) {
    }

    // Wait a few seconds before showing the prompt
    setTimeout(() => {
      showInstallPrompt.value = true;
    }, 5000);
  });

  // Listen for the appinstalled event to track successful installations
  window.addEventListener('appinstalled', (e) => {
    installState.value = 'installed';
    // Hide the prompt after successful installation
    showInstallPrompt.value = false;
  });
});

onUnmounted(() => {
  // Clean up event listener
  window.removeEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferredPrompt.value = e;
  });
});
</script>

<template>
  <div v-if="showInstallPrompt" class="install-prompt">
    <div class="install-prompt-content">
      <div class="install-prompt-header">
        <img src="/pwa/pwa-192x192.png" alt="BoxBuddy" class="app-icon" />
        <div class="install-prompt-title">
          <h3>Install BoxBuddy</h3>
          <p>Add to your home screen for easy access</p>
        </div>
        <button @click="dismissPrompt" class="close-button">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </div>

      <div v-if="isIOS" class="ios-instructions">
        <p>To install this app on your iPhone:</p>
        <ul class="list-disc text-black">
          <li>Tap the <strong>Share</strong> button <svg class="inline-block" xmlns="http://www.w3.org/2000/svg"
              width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
              stroke-linecap="round" stroke-linejoin="round">
              <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"></path>
              <polyline points="16 6 12 2 8 6"></polyline>
              <line x1="12" y1="2" x2="12" y2="15"></line>
            </svg></li>
          <li>Scroll down and tap <strong>Add to Home Screen</strong></li>
          <li>Tap <strong>Add</strong> in the top right corner</li>
        </ul>
        <button @click="dismissPrompt" class="install-button">Got it</button>
      </div>

      <div v-else-if="isAndroid" class="android-instructions">
        <p>Add BoxBuddy to your home screen for quick access to local businesses and easy ordering.</p>
        <div class="android-features">
          <div class="feature">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
              stroke="#4CAF50" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="10"></circle>
              <polyline points="12 6 12 12 16 14"></polyline>
            </svg>
            <span>Faster access</span>
          </div>
          <div class="feature">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
              stroke="#4CAF50" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M18 8h1a4 4 0 0 1 0 8h-1"></path>
              <path d="M2 8h16v9a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4V8z"></path>
              <line x1="6" y1="1" x2="6" y2="4"></line>
              <line x1="10" y1="1" x2="10" y2="4"></line>
              <line x1="14" y1="1" x2="14" y2="4"></line>
            </svg>
            <span>Works offline</span>
          </div>
          <div class="feature">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
              stroke="#4CAF50" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
              <polyline points="22 4 12 14.01 9 11.01"></polyline>
            </svg>
            <span>No app store needed</span>
          </div>
        </div>
        <button @click="installApp" class="install-button" :disabled="installState === 'installing'">
          <span v-if="installState === 'idle'">Install App</span>
          <span v-else-if="installState === 'installing'">Installing...</span>
          <span v-else>Installed!</span>
        </button>
        <button @click="dismissPrompt" class="dismiss-button">Maybe Later</button>
      </div>

      <div v-else class="flex flex-col items-center">
        <p class="mb-4 text-white dark:text-gray-600">Install BoxBuddy for a better experience:</p>
        <button @click="installApp" class="install-button">Add to Home Screen</button>
        <button @click="dismissPrompt" class="dismiss-button">Not Now</button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.install-prompt {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: white;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
  z-index: 9999999999999999999999999999999999999;
  border-top-left-radius: 16px;
  border-top-right-radius: 16px;
  padding: 16px;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  transform: translateY(100%);
  animation: slideUp 0.3s ease-out forwards;
}

@keyframes slideUp {
  to {
    transform: translateY(0);
  }
}

.install-prompt-content {
  width: 100%;
  max-width: 600px;
  margin: 0 auto;
  padding: 0 8px;
}

.install-prompt-header {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
  gap: 12px;
}

.app-icon {
  width: 48px;
  height: 48px;
  border-radius: 8px;
  flex-shrink: 0;
}

.install-prompt-title {
  flex: 1;
  min-width: 0;
}

.install-prompt-title h3 {
  margin: 0;
  font-size: 18px;
  color: #333;
  line-height: 1.2;
}

.install-prompt-title p {
  margin: 4px 0 0;
  font-size: 14px;
  color: #666;
  line-height: 1.3;
}

.close-button {
  background: none;
  border: none;
  color: #999;
  cursor: pointer;
  padding: 4px;
  flex-shrink: 0;
}

.ios-instructions, .android-instructions, .other-browser-instructions {
  padding: 8px 0;
}

.ios-instructions p, .android-instructions p, .other-browser-instructions p {
  margin: 0 0 16px;
  font-size: 14px;
  color: #333;
  line-height: 1.4;
}

.ios-instructions ul {
  margin: 0 0 16px;
  padding-left: 24px;
}

.ios-instructions li {
  margin-bottom: 8px;
  font-size: 14px;
  line-height: 1.4;
}

.ios-instructions svg {
  vertical-align: middle;
}

/* Android-specific styles */
.android-features {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(80px, 1fr));
  gap: 12px;
  margin-bottom: 16px;
}

.feature {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 8px 4px;
}

.feature svg {
  margin-bottom: 8px;
  flex-shrink: 0;
}

.feature span {
  font-size: 12px;
  color: #666;
  line-height: 1.2;
}

.install-button {
  background-color: #4CAF50;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 12px 24px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  margin-bottom: 8px;
  transition: background-color 0.2s ease;
}

.install-button:hover {
  background-color: #45a049;
}

.install-button:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}

.dismiss-button {
  background-color: transparent;
  color: #666;
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 12px 24px;
  font-size: 16px;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.dismiss-button:hover {
  background-color: #f5f5f5;
}

/* Mobile-specific adjustments */
@media (max-width: 480px) {
  .install-prompt {
    padding: 12px;
  }

  .install-prompt-content {
    padding: 0 4px;
  }

  .install-prompt-header {
    gap: 8px;
  }

  .app-icon {
    width: 40px;
    height: 40px;
  }

  .install-prompt-title h3 {
    font-size: 16px;
  }

  .install-prompt-title p {
    font-size: 13px;
  }

  .android-features {
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;
  }

  .feature {
    padding: 4px 2px;
  }

  .feature svg {
    width: 20px;
    height: 20px;
  }

  .feature span {
    font-size: 11px;
  }

  .install-button, .dismiss-button {
    padding: 10px 20px;
    font-size: 15px;
  }
}

/* Ensure proper spacing on very small screens */
@media (max-width: 360px) {
  .install-prompt {
    padding: 8px;
  }

  .install-prompt-content {
    padding: 0 2px;
  }

  .android-features {
    grid-template-columns: repeat(3, 1fr);
    gap: 6px;
  }

  .feature span {
    font-size: 10px;
  }
}
</style>
